/**
 * Full SSR (treatment) entry for the landing welcome route. Mounted
 * via `dynamicWithExperiment` from `routes/landing.welcome._index.tsx`
 * whenever the global `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment
 * resolves to `treatment`.
 *
 * Mirrors the previous inline `default` Component:
 * - Provides a route-scoped empty Apollo client so hooks like
 *   `useMyInfo` inside `useNavigateToGenerator` can mount during SSR
 *   without throwing "Could not find 'client' in the context".
 * - Reads `loaderData.initialPaymentItems` (cache-only on the server)
 *   and threads it down to `LandingHome` so `PricingSection` renders
 *   real prices on the first SSR flush instead of the
 *   `PaidPlanCardFallback` skeleton.
 *
 * The CSR (control) entry lives in `landing.welcome._index.csr.tsx`
 * and renders `<LandingHome />` directly under the global
 * `<RootRuntime>` Apollo client; it skips the SSR pricing seed.
 */
import { ApolloProvider } from '@apollo/client/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useLoaderData } from 'react-router'
import LandingHome from 'src/features/Landing/Welcome/components/LandingHome'
import type { loader } from 'src/routes/landing.welcome._index'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import { useRequestContext } from 'src/utils/request-context'

export default function LandingWelcomeFullSSREntry() {
  const { queryClient } = useRequestContext()
  const loaderData = useLoaderData<typeof loader>()
  const apolloClient = useMemo(() => createRouteApolloClient([]), [])

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={apolloClient}>
        <LandingHome initialPaymentItems={loaderData.initialPaymentItems} />
      </ApolloProvider>
    </QueryClientProvider>
  )
}
